import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import SEOHead from "../components/common/SEOHead";
import { fetchItchioGameData } from "../utils/itchioApi";

// Submissions data with itch.io URLs - manually categorized
const submissionsData = {
  school: [
    {
      id: 1,
      itchioUrl:
        "https://hashintha-ranasinghe.itch.io/float-in-space-2d-arcade-sci-fi-survival-game",
      category: "school",
    },
    {
      id: 2,
      itchioUrl: "https://hirujaedurapola.itch.io/the-bridge-of-letters",
      category: "school",
    },
    {
      id: 3,
      itchioUrl: "https://team-kairo.itch.io/floatlands",
      category: "school",
    },
    {
      id: 4,
      itchioUrl: "https://itzmcboss.itch.io/knight-of-the-sky",
      category: "school",
    },
    {
      id: 5,
      itchioUrl: "https://manuja-xo61.itch.io/hollow-blight-havenwood",
      category: "university",
    },
    {
      id: 6,
      itchioUrl: "https://aditha-j.itch.io/th",
      category: "university",
    },
    {
      id: 7,
      itchioUrl: "https://heshan-banneka.itch.io/journey",
      category: "university",
    },
  ],
  university: [
    {
      id: 11,
      itchioUrl: "https://kanishkagayanath.itch.io/feather-drift-v1",
      category: "university",
    },
    {
      id: 12,
      itchioUrl: "https://sulithanulaksha.itch.io/operation-firefly",
      category: "university",
    },
    {
      id: 13,
      itchioUrl: "https://disumi.itch.io/letters-of-lavender",
      category: "university",
    },
    {
      id: 14,
      itchioUrl: "https://sandaruneth.itch.io/sunbane",
      category: "university",
    },
    {
      id: 15,
      itchioUrl: "https://geez-zz.itch.io/contactless",
      category: "university",
    },
    {
      id: 16,
      itchioUrl: "https://kidfromjupiter.itch.io/chrono-strike",
      category: "university",
    },
    {
      id: 17,
      itchioUrl: "https://game3studios.itch.io/the-shadowslisten",
      category: "university",
    },
    {
      id: 18,
      itchioUrl: "https://alarmnarrow.itch.io/whispers-in-the-woods",
      category: "university",
    },
    {
      id: 19,
      itchioUrl: "https://sachindu-nethmin.itch.io/trigger",
      category: "university",
    },
    {
      id: 20,
      itchioUrl: "https://syntax-squad.itch.io/legacyofkashyapa",
      category: "university",
    },
    {
      id: 21,
      itchioUrl: "https://mhashhash.itch.io/hazard-run",
      category: "university",
    },
    {
      id: 22,
      itchioUrl: "https://sahan-viranga.itch.io/uracing-susl",
      category: "university",
    },
    {
      id: 23,
      itchioUrl: "https://alien740.itch.io/tnats",
      category: "university",
    },
    {
      id: 24,
      itchioUrl: "https://sakeen.itch.io/silent-heist",
      category: "university",
    },
    {
      id: 25,
      itchioUrl: "https://kalani-chandrasena.itch.io/sneaky-pizza-drop",
      category: "university",
    },
    {
      id: 26,
      itchioUrl: "https://manujagma.itch.io/thelastbox",
      category: "university",
    },
    {
      id: 27,
      itchioUrl: "https://need-to-touch-some-grass.itch.io/pillers-of-radiance",
      category: "university",
    },
    {
      id: 28,
      itchioUrl: "https://udantha.itch.io/zentraks",
      category: "university",
    },
    {
      id: 29,
      itchioUrl: "https://senesh-dinelka.itch.io/a-day-in-may-2025",
      category: "university",
    },
    {
      id: 30,
      itchioUrl: "https://kasunyahimani.itch.io/emotisphere",
      category: "university",
    },
    {
      id: 31,
      itchioUrl:
        "https://veridiumlatte.itch.io/inside-job-medical-student-edition",
      category: "university",
    },
  ],
};

const Submissions = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [gameData, setGameData] = useState({});
  const [loading, setLoading] = useState(true);

  // Category tabs
  const categoryTabs = [
    {
      id: "all",
      name: "All Games",
      icon: "grid-outline",
      count: submissionsData.school.length + submissionsData.university.length,
    },
    {
      id: "school",
      name: "School Category",
      icon: "school-outline",
      count: submissionsData.school.length,
    },
    {
      id: "university",
      name: "University Category",
      icon: "library-outline",
      count: submissionsData.university.length,
    },
  ];

  // Get submissions for selected category
  const getFilteredSubmissions = () => {
    if (selectedCategory === "all") {
      return [...submissionsData.school, ...submissionsData.university];
    }
    return submissionsData[selectedCategory] || [];
  };

  // Fetch game data from itch.io URLs
  useEffect(() => {
    const fetchAllGameData = async () => {
      setLoading(true);
      const allSubmissions = [
        ...submissionsData.school,
        ...submissionsData.university,
      ];
      const gameDataMap = {};

      for (const submission of allSubmissions) {
        try {
          const data = await fetchItchioGameData(submission.itchioUrl);
          gameDataMap[submission.id] = data;
        } catch (error) {
          console.error(
            `Error fetching data for ${submission.itchioUrl}:`,
            error
          );
          gameDataMap[submission.id] = {
            title: "Game Title Unavailable",
            thumbnail: null,
            author: "Unknown",
            originalUrl: submission.itchioUrl,
            isValid: false,
          };
        }
      }

      setGameData(gameDataMap);
      setLoading(false);
    };

    fetchAllGameData();
  }, []);

  // Game Card Component
  const GameCard = ({ submission, index }) => {
    const gameInfo = gameData[submission.id] || {};

    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        viewport={{ once: true }}
        className="exe-glass-card overflow-hidden h-full flex flex-col"
      >
        {/* Game Thumbnail */}
        <div className="relative h-48 bg-gradient-to-br from-exe-text-accent/20 to-exe-bg-secondary">
          {gameInfo.thumbnail ? (
            <img
              src={gameInfo.thumbnail}
              alt={gameInfo.title || "Game thumbnail"}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}

          {/* Fallback when no thumbnail */}
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ display: gameInfo.thumbnail ? "none" : "flex" }}
          >
            <div className="text-center">
              <ion-icon
                name="game-controller"
                class="text-5xl text-exe-text-accent mb-2"
              ></ion-icon>
              <p className="text-exe-text-secondary text-sm">Game Thumbnail</p>
              <p className="text-exe-text-secondary text-xs">Not Available</p>
            </div>
          </div>

          {/* Category Badge */}
          <div className="absolute top-3 left-3">
            <div className="bg-exe-text-accent text-exe-bg-primary text-xs font-bold px-3 py-1 rounded-full capitalize">
              {submission.category}
            </div>
          </div>
        </div>

        {/* Game Info */}
        <div className="p-6 flex-grow flex flex-col">
          <div className="mb-4">
            <h3 className="text-xl font-bold text-exe-text-accent mb-2">
              {gameInfo.title || "Loading..."}
            </h3>
            {gameInfo.author && (
              <p className="text-exe-text-secondary text-sm mb-2">
                by {gameInfo.author}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-auto space-y-3">
            <a
              href={submission.itchioUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-exe-text-accent text-exe-bg-primary text-center py-3 px-4 rounded-lg font-semibold transition-all duration-200 hover:bg-exe-text-accent/90 hover:scale-[1.02]"
            >
              <ion-icon name="play" class="mr-2"></ion-icon>
              Play on itch.io
            </a>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <HelmetProvider>
      <div className="exe-bg-pattern min-h-screen pt-20">
        <SEOHead
          title="EXE Competition Submissions & Winners"
          description="Discover the winning games and submissions from EXE - Sri Lanka's premier game development competition. View award-winning games and their creators."
          keywords="EXE submissions, game development winners, Sri Lanka games, competition results, itch.io games"
        />

        <div className="exe-container">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="exe-title text-exe-text-accent mb-6">
              Competition Submissions & Winners
            </h1>
            <p className="exe-text text-xl max-w-3xl mx-auto">
              Explore the incredible games created during EXE competition. From
              innovative gameplay to stunning visuals, these submissions
              represent the best of Sri Lankan game development talent.
            </p>
          </motion.div>

          {/* Category Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-2 mb-12"
          >
            {categoryTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedCategory(tab.id)}
                className={`flex items-center px-6 py-3 rounded-lg transition-all duration-200 ${
                  selectedCategory === tab.id
                    ? "bg-exe-text-accent text-exe-bg-primary font-semibold"
                    : "bg-exe-text-accent/10 text-exe-text-primary hover:bg-exe-text-accent/20"
                }`}
              >
                <ion-icon name={tab.icon} class="mr-2"></ion-icon>
                {tab.name}
                <span className="ml-2 bg-exe-text-accent/20 text-xs px-2 py-1 rounded-full">
                  {tab.count}
                </span>
              </button>
            ))}
          </motion.div>

          {/* Loading State */}
          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-exe-text-accent mb-4"></div>
              <p className="exe-text">Loading game data from itch.io...</p>
            </motion.div>
          )}

          {/* Games Grid */}
          {!loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {getFilteredSubmissions().map((submission, index) => (
                <GameCard
                  key={submission.id}
                  submission={submission}
                  index={index}
                />
              ))}
            </motion.div>
          )}

          {/* Empty State */}
          {!loading && getFilteredSubmissions().length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <ion-icon
                name="game-controller-outline"
                class="text-6xl text-exe-text-accent/50 mb-4"
              ></ion-icon>
              <h3 className="text-xl font-semibold text-exe-text-primary mb-2">
                No submissions found
              </h3>
              <p className="exe-text">
                No games have been submitted in this category yet.
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </HelmetProvider>
  );
};

export default Submissions;
